/**
 * Pure selection math behind the invoices-list bulk-action bar (design:
 * docs/superpowers/specs/2026-08-17-bulk-invoice-actions-design.md §1-3).
 * No React, no fetch — InvoicesPage and its bottom bar/confirm dialogs render
 * this; they do not reimplement the eligibility rules.
 *
 * `@repo/shared-types` re-exports `Invoice` (and its `status` field) as
 * type-only, so eligibility here is checked against plain string literals —
 * same convention InvoicesPage's own status filter already uses.
 */

/** Minimal shape the selection-bar math needs, so callers can pass rows
 *  straight out of the invoices list query untouched. */
export interface SelectableInvoice {
  id: string;
  status: string;
  totalAmount: number;
}

/** Mirrors `isInvoicePayable` in `apps/api/src/services/recordPayment.ts` —
 *  the actual gate `POST /invoices/bulk/mark-paid` enforces server-side —
 *  in exclusion form rather than as an allowlist, so this can't quietly fall
 *  out of sync the day a new status is introduced or `SENT` starts getting
 *  assigned (today it never is, which is the only reason an allowlist of
 *  {DRAFT, PENDING, PARTIAL} would look extensionally equal to this). DRAFT,
 *  PENDING, PARTIAL, and 0đ invoices all pass this the same way the backend
 *  passes them. OVERDUE is derived, never stored (Invoice.isOverdue), so an
 *  overdue PENDING is still just PENDING here — eligibility never looks at
 *  isOverdue, only at `status`. Only PAID/CANCELLED are excluded. */
export function isMarkPaidEligible(status: string): boolean {
  return status !== 'PAID' && status !== 'CANCELLED';
}

/** Bulk delete is DRAFT-only by decision (design doc §3): PAID/PENDING
 *  invoices are money records — a fat-fingered bulk delete of those is the
 *  expensive kind of mistake. */
export function isBulkDeleteEligible(status: string): boolean {
  return status === 'DRAFT';
}

export interface BulkActionPartition {
  /** How many of the passed invoices the action will act on. */
  eligibleCount: number;
  /** How many it will skip — the confirm dialog shows this before the call
   *  is ever made; the API's own `skipped` count on the response should
   *  agree (mixed selections are legal, executing acts on the eligible
   *  subset only). */
  skippedCount: number;
}

function partitionBy(
  invoices: SelectableInvoice[],
  predicate: (status: string) => boolean,
): BulkActionPartition {
  const eligibleCount = invoices.reduce((n, inv) => n + (predicate(inv.status) ? 1 : 0), 0);
  return { eligibleCount, skippedCount: invoices.length - eligibleCount };
}

export function partitionForMarkPaid(invoices: SelectableInvoice[]): BulkActionPartition {
  return partitionBy(invoices, isMarkPaidEligible);
}

export function partitionForBulkDelete(invoices: SelectableInvoice[]): BulkActionPartition {
  return partitionBy(invoices, isBulkDeleteEligible);
}

/** Plain sum of the selected invoices' totals — the selection bar's headline
 *  number. No proration and no exclusion of ineligible rows: what's ticked
 *  is what's summed, exactly as the design doc specifies. */
export function sumSelectedTotals(invoices: SelectableInvoice[]): number {
  return invoices.reduce((sum, inv) => sum + Number(inv.totalAmount), 0);
}
